// handle "/" in file name
import { useEffect, useMemo, useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import { saveAs } from "file-saver";
import "codemirror/mode/php/php";
import "codemirror/lib/codemirror.css";
import "codemirror/theme/material.css";
import "codemirror/addon/edit/matchbrackets"; // Import the addon
import "codemirror/addon/search/searchcursor"; // Import the addon
import "codemirror/addon/comment/comment"; // Import the addon
import { Controlled as CodeMirror } from "react-codemirror2";
//-------------------------------------------------
import { ReactComponent as MatchCase }  from "src/img/svg/matchCase.svg";
import { ReactComponent as MatchWholeWord }  from "src/img/svg/matchWholeWord.svg";
import { ReactComponent as ReplaceAll }  from "src/img/svg/replaceAll.svg";
import { ReactComponent as Replace }  from "src/img/svg/replace.svg";
import magnifierImg from "src/img/magnifier.png";
import Resize from "src/components/common/resize";
import Modal from "src/components/common/modal";
import FileIcon from "src/components/common/fileIcons";
import Footer from "src/components/common/frame/footer";
import { useMyStore } from "src/components/main/mainStore";
import {
    MyFormData, 
    myAxios, 
    getTimeStamp, 
    scroll,
    isDevMode,
    appTitle, 
} from "src/components/common/functions";
export const invalidFileNameChars = ["\\" /* only this not allowed in linux */, "/", ":", "*", "?", "\"", "<", "<", "|"];
export const defaultFolder = "/besstestlab";
export const defaultFolders = [
    {label: "Bess Utility Solutions", value: "/besstestlab"}, 
    {label: "Material Testing Inspection", value: "/bessmti"}
]
export const paramlists = {
    "path": [], 
    "searchBar": ["true"],
    "matchCase": ["true"],
    "matchWholeWold": ["true"],
    "showReplaceBar": ["true"],
    "searchQuery": [],
    "replaceQuery": [],
};
//-------------------------------------------------
export default function FileExplorer({ tabTitle }){
    const history = useHistory();
    const params = useMemo(() => new URLSearchParams(history.location.search), [history.location]);
    const filePath = params.get("path");
    const setShowLoader = useMyStore(state => state.setShowLoader);
    const curUser = useMyStore(state => state.user);
    //-------------------------------------------------
    const [updateDateTime, setUpdateDateTime] = useState(getTimeStamp());
    const [fileName, setFileName] = useState("");
    const [data, setData] = useState([]);
    const [showLoading, setShowLoading] = useState(false);
    const [renderSwitch, setRenderSwitch] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [content, setContent] = useState("");
    const [initialContent, setInitialContent] = useState("");
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10);
    const [filterWords, setFilterWords] = useState("");
    //-------------------------------------------------
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const filteredData = data.filter(item => filterWords === ""?true:JSON.stringify(item).toLowerCase().includes(filterWords));
    const currentPageItems = filteredData.slice(indexOfFirstItem, indexOfLastItem);
    const pageNumbers = Array.from({length: Math.ceil(filteredData.length / itemsPerPage)}, (_, i) => (i + 1));
    const prevDependencies = useRef({ renderSwitch, updateDateTime });
    useEffect(() => {
        if(
            prevDependencies.current.renderSwitch === renderSwitch && /* renderSwitch not change */
            prevDependencies.current.updateDateTime === updateDateTime && /* updateDateTime not change */
            history.action === "REPLACE" && (history.location.state === undefined || history.location.state.external !== true) /* internal `history.replace`, not from log in */
        ) return;
        let isMounted = true;
        const getData = async () => {
            if(isDevMode) console.log("useEffect");
            //------------------------
            setShowLoading(true);
            const formData = new MyFormData();
            if(filePath !== null) formData.append("filePath", filePath);
            else{
                formData.append("filePath", "");
            }
            const res = await myAxios("server/fileExplorer.php", formData);
            if(!isMounted) return;
            setShowLoading(false);
            if(!res) return;
            if(res.data.type === "dir"){
                setShowModal(false);
                setData(res.data.content);
                if(res.data.isFallBack){
                    history.replace({search: "?path=" + defaultFolder});
                }else{
                    if(params.size !== 1)  history.replace({search: "?path=" + params.get("path")});
                }
            }else if(res.data.type === "file"){
                setShowModal(true);
                setFileName(filePath.split("/").at(-1));
                setContent(typeof(res.data.content) !== "string"?JSON.stringify(res.data.content):res.data.content);
                setInitialContent(typeof(res.data.content) !== "string"?JSON.stringify(res.data.content):res.data.content);
                params.forEach((value, key, parent) => {
                    if(
                        !(key in paramlists) || 
                        (paramlists[key].length && !paramlists[key].includes(value)) || 
                        parent.get(key) === ""
                    ) parent.delete(key);
                });
                history.replace({search: decodeURIComponent(params.toString())});
            }
        }
        getData();
        prevDependencies.current = { renderSwitch, updateDateTime };
        return () => {
            isMounted = false;
            setShowLoader(false);
        }
    }, [renderSwitch, filePath, updateDateTime, params, history, setShowLoader]);
    useEffect(() => {
        document.title = tabTitle;
        return () => {
            document.title = appTitle;
        };
    }, [tabTitle]);
    //-------------------------------------------------
    return (
        <div className = "cssTab">
            <div className = "cssHeader" style = {{backgroundColor: curUser.userTheme}}>
                <span>
                    <Resize/>
                    {tabTitle}
                    <span data-toggle = "tooltip" title = {"Refreshed on " + updateDateTime} className = "btn btn-primary py-1 ml-4" onClick = {() => setUpdateDateTime(getTimeStamp())}>↻</span>
                </span>
                <span>
                    <input
                        className = "form-control w-auto"
                        id = "filterWords"
                        type = "text"
                        value = {filterWords}
                        onChange = {e => {
                            setFilterWords(e.currentTarget.value.toLowerCase());
                        }}
                    />
                    <img
                        alt = ""
                        src = {magnifierImg}
                    />
                </span>
                {filePath !== null?
                <span className = "d-flex align-items-center">
                    <label htmlFor = "path" className = "mb-0">Path:</label>
                    <select
                        id = "path"
                        className = "form-control w-auto ml-2"
                        value = {"/" + filePath.split("/")[1]}
                        onChange = {e => {
                            history.push({search: "?path=" + e.currentTarget.value});
                        }}
                    >
                        {defaultFolders.map((item, itemIndex) => <option key = {itemIndex} value = {item.value}>{item.label}</option>)}
                    </select>
                </span>
                :<></>}
                <label className = "pointer" htmlFor = "file">
                    Upload File
                    <input
                        id = "file"
                        type = "file"
                        multiple = {true}
                        className = "nodisplay"
                        onChange = {async e => {
                            const target = e.currentTarget;
                            const files = target.files;
                            if(files.length === 0 || files[0].name === "") return;
                            const formData = new MyFormData();
                            for(const file of files){
                                formData.append("files[]", file);
                            }
                            formData.append("action", "upload");
                            formData.append("filePath", filePath);
                            setShowLoader(true);
                            const res = await myAxios("server/fileExplorer.php", formData);
                            setShowLoader(false);
                            if(!res) return;
                            setRenderSwitch(s => !s);
                            target.value = "";
                        }}
                    />
                </label>
                <span className = "pointer" onClick = {async() => {
                    const userInput = window.prompt("The new file name:");
                    if(userInput === null || userInput === "") return;
                    for(let char of ["\\" /* only this not allowed in linux */, "/", ":", "*", "?", "\"", "<", "<", "|"]){
                        if(userInput.indexOf(char) > -1){
                            if(char === "\"") window.alert(`${char} is not allowed for file name.`);
                            else window.alert(`"${char}" is not allowed for file name.`);
                            return;
                        }
                    }
                    const formData = new MyFormData();
                    formData.append("action", "createFile");
                    formData.append("filePath", filePath);
                    formData.append("fileName", userInput);
                    const res = await myAxios("server/fileExplorer.php", formData)
                    if(!res) return;
                    setRenderSwitch(s => !s);
                }}>Add File</span>
                <span className = "pointer" onClick = {async() => {
                    const userInput = window.prompt("The new folder name:");
                    if(userInput === null || userInput === "") return;
                    for(let char of invalidFileNameChars){
                        if(userInput.indexOf(char) > -1){
                            if(char === "\"") window.alert(`${char} is not allowed for folder name.`);
                            else window.alert(`"${char}" is not allowed for folder name.`);
                            return;
                        }
                    }
                    const formData = new MyFormData();
                    formData.append("action", "createFolder");
                    formData.append("filePath", filePath);
                    formData.append("fileName", userInput);
                    const res = await myAxios("server/fileExplorer.php", formData)
                    if(!res) return;
                    setRenderSwitch(s => !s);
                }}>Add Folder</span>
            </div>
            <div className = "cssContent pb10">
                {showLoading?<div className = "cssLoading">Loading...</div>:
                !filteredData.length?<div className = "cssLoading">Empty</div>:
                <>
                <div className = "h4 my-3">
                    {filePath !== null?
                    filePath.split("/").slice(1).map((part, partIndex, self) => 
                        <span
                            key = {partIndex}
                            className = "text-success"
                            onClick = {() => {
                                const thisPath = "/" + filePath.split("/").slice(1).slice(0, partIndex + 1).join("/");
                                history.push({search: "?path=" + thisPath});
                            }}
                        >
                            /
                            <span
                                className = {partIndex === self.length - 1?"":"pointer text-decoration-underline"}
                            >
                                {part}
                            </span>
                        </span>
                    )
                    :null}
                </div>
                {currentPageItems.map((item, index) => 
                <div key = {index} className = "h5">
                    <span
                        className = {`pointer${item.isFolder?" text-primary":""}`}
                        onClick = {async () => {
                            if(item.isFolder) scroll(null);
                            history.push({search: "?path=" + filePath + "/" + item.path});
                            setCurrentPage(1);
                        }}
                    >
                        <FileIcon
                            fileType = {item.path.split(".").at(-1)} 
                            isFolder = {item.isFolder} 
                            className = "mr-2"
                        />
                        {item.path}
                    </span>
                    <span
                        className = "ml-2 btn btn-warning"
                        onClick = {async() => {
                            if(!window.confirm("Are you sure to rename this " + (item.isFolder?"folder":"file") + "?")) return;
                            const userInput = window.prompt(item.path + "\nupdated " + (item.isFolder?"folder":"file") + " name:", item.path);
                            if(userInput === null || userInput === "") return;
                            const formData = new MyFormData();
                            formData.append("action", "rename");
                            formData.append("filePath", filePath);
                            formData.append("fileName", item.path);
                            formData.append("newFileName", userInput);
                            //------------------------
                            setShowLoader(true);
                            const res = await myAxios("server/fileExplorer.php", formData);
                            setShowLoader(false);
                            if(!res) return;
                            //------------------------
                            setRenderSwitch(s => !s);
                        }}
                    >
                        rename
                    </span>
                    <span
                        className = "ml-2 btn btn-warning"
                        onClick = {async() => {
                            if(!window.confirm("Are you sure to copy this " + (item.isFolder?"folder":"file") + "?")) return;
                            const userInput = window.prompt(item.path + "\ncopied " + (item.isFolder?"folder":"file") + " name:");
                            if(userInput === null || userInput === "") return;
                            const formData = new MyFormData();
                            formData.append("action", "copy");
                            formData.append("filePath", filePath);
                            formData.append("fileName", item.path);
                            formData.append("newFileName", userInput);
                            //------------------------
                            setShowLoader(true);
                            const res = await myAxios("server/fileExplorer.php", formData);
                            setShowLoader(false);
                            if(!res) return;
                            //------------------------
                            setRenderSwitch(s => !s);
                        }}
                    >
                        copy
                    </span>
                    <span
                        className = "ml-2 btn btn-warning"
                        onClick = {async() => {
                            if(!window.confirm("Are you sure to move this " + (item.isFolder?"folder":"file") + "?")) return;
                            const userInput = window.prompt(item.path + "\nupdated " + (item.isFolder?"folder":"file") + " path:");
                            if(userInput === null || userInput === "") return;
                            const formData = new MyFormData();
                            formData.append("action", "move");
                            formData.append("filePath", filePath);
                            formData.append("fileName", item.path);
                            formData.append("newPath", userInput);
                            //------------------------
                            setShowLoader(true);
                            const res = await myAxios("server/fileExplorer.php", formData);
                            setShowLoader(false);
                            if(!res) return;
                            //------------------------
                            setRenderSwitch(s => !s);
                        }}
                    >
                        move
                    </span>
                    <span
                        className = "ml-2 btn btn-info"
                        onClick = {async() => {
                            const formData = new MyFormData();
                            formData.append("action", "download");
                            formData.append("filePath", filePath);
                            formData.append("fileName", item.path);
                            //------------------------
                            setShowLoader(true);
                            const res = await myAxios("server/fileExplorer.php", formData, "blob");
                            setShowLoader(false);
                            if(!res) return;
                            saveAs(res.data, item.isFolder?item.path + ".zip":item.path);
                        }}
                    >
                        download
                    </span>
                    <span
                        className = "ml-2 btn btn-info"
                        onClick = {async() => {
                            const formData = new MyFormData();
                            formData.append("action", "property");
                            formData.append("filePath", filePath + "/" + item.path);
                            //------------------------
                            setShowLoader(true);
                            const res = await myAxios("server/fileExplorer.php", formData);
                            setShowLoader(false);
                            if(!res) return;
                            window.alert(res.data);
                            //------------------------
                        }}
                    >
                        property
                    </span>
                    <span
                        className = "ml-2 btn btn-danger"
                        onClick = {async() => {
                            if(!window.confirm("Are you sure to delete this " + (item.isFolder?"folder":"file") + "?")) return;
                            const formData = new MyFormData();
                            formData.append("action", "delete");
                            formData.append("filePath", filePath + "/" + item.path);
                            //------------------------
                            setShowLoader(true);
                            const res = await myAxios("server/fileExplorer.php", formData);
                            setShowLoader(false);
                            if(!res) return;
                            //------------------------
                            setRenderSwitch(s => !s);
                        }}
                    >
                        delete
                    </span>
                </div>)}
                </>}
                {!showLoading && pageNumbers.length?
                <Footer
                    length = {filteredData.length}
                    pageNumbers = {pageNumbers}

                    currentPage = {currentPage}
                    setCurrentPage = {setCurrentPage}
                    itemsPerPage = {itemsPerPage}
                    setItemsPerPage = {setItemsPerPage}
                />
                :null}
            </div>
            {showModal?
            <CodeEditor
                setShowModal = {setShowModal} 
                setFileName = {setFileName} 
                setContent = {setContent} 
                setInitialContent = {setInitialContent}
                fileName = {fileName} 
                filePath = {filePath} 
                content = {content}
                initialContent = {initialContent}
                history = {history}
                setShowLoader = {setShowLoader}
            />
            :<></>}
        </div>
    )
}

function CodeEditor({
    setShowModal, 
    setFileName, 
    setContent, 
    setInitialContent, 
    fileName, 
    filePath, 
    content, 
    initialContent, 
    history
}){
    const params = new URLSearchParams(history.location.search);
    const showSearchBar = params.get("searchBar") === "true"?true:false;
    const searchQuery = params.get("searchQuery") ?? ""; // null | String
    const matchCase = params.get("matchCase") === "true"?true:false;
    const matchWholeWold = params.get("matchWholeWold") === "true"?true:false;
    const replaceQuery = params.get("replaceQuery") ?? ""; // null | String
    const showReplaceBar = params.get("showReplaceBar") === "true"?true:false;
    const editorRef = useRef(null);
    const searchBarInputRef = useRef(null);
    // const editorInstanceRef = useRef(null);
    const [markers, setMarkers] = useState([]); // To store highlighted markers
    const [matchCount, setMatchCount] = useState(0);
    const [searchIndex, setSearchIndex] = useState(0);
    const [typingTimeOut, setTypingTimeOut] = useState();
    const [searchText, setSearchText] = useState(searchQuery);
    const handleSubmit = async() => {
        if(content === initialContent) return;
        const formData = new MyFormData();
        formData.append("action", "update");
        formData.append("filePath", filePath);
        formData.append("content", content);
        const res = await myAxios("server/fileExplorer.php", formData);
        if(!res) return;
        setInitialContent(content);
    }
    const closeIt = () => {
        if(content !== initialContent && !window.confirm(`Do you want to leave without saving the changes you made to "${filePath}"`)) return;
        setShowModal(false);
        setFileName("");
        setContent("");
        setInitialContent("");
        history.push({search: "?path=" + filePath.split("/").slice(0, -1).join("/")});
    }
    useEffect(() => {
        if(editorRef.current){
            const { editor } = editorRef.current;
            editor.focus(); // Focus the editor
            // editor.setCursor({ line: 0, ch: 0 }); // Set cursor to the start
            const lastLine = editor.lastLine(); // Get the last line number
            const lastChar = editor.getLine(lastLine).length; // Get the last character in the last line
            editor.setCursor({ line: lastLine, ch: lastChar }); // Set the cursor at the end
        }
    }, []);
    useEffect(() => {
        const getData = async() => {
            await markers.forEach((marker) => marker.clear()); // Clear each marker
            const editor = editorRef.current.editor; // Access the CodeMirror instance
            const existingCursor = editor.getCursor();  // Get the current cursor position
            editor.setSelection(existingCursor, existingCursor); 
            if(markers.length) setMarkers([]);
            setMatchCount(0);
            if(!editor || !searchQuery) return;
            // --------------------------------------------------------------------- 
            const regex = !matchWholeWold && !matchCase ? new RegExp(searchQuery.replace(/[-/\\^$*+?.()|[\]{}]/g, "\\$&"), "gi") : 
                matchWholeWold && !matchCase ? new RegExp(`\\b${searchQuery.replace(/[-/\\^$*+?.()|[\]{}]/g, "\\$&")}\\b`, "gi") : 
                !matchWholeWold && matchCase ? new RegExp(searchQuery.replace(/[-/\\^$*+?.()|[\]{}]/g, "\\$&"), "g") : 
                new RegExp(`\\b${searchQuery.replace(/[-/\\^$*+?.()|[\]{}]/g, "\\$&")}\\b`, "g");
            // --------------------------------------------------------------------- 
            const cursor = editor.getSearchCursor(regex, null);
            // Find and highlight the search result
            const newMarkers = [];
            let count = 0;
            while(cursor.findNext()){
                const marker = editor.markText(cursor.from(), cursor.to(), {className: searchIndex === count?"curHighlight":"highlight"});
                if(searchIndex === count){
                    // editor.setSelection(cursor.from(), cursor.to()); // Highlight the result
                    // const object = cursor.from();
                    // const fixedBarHeight = document.querySelector("#fixed-bar").offsetHeight;
                    editor.scrollIntoView(cursor.from()); // Scroll to the result
                    // cursor.from().scrollIntoView({ behavior: "smooth", block: "center" });
                    // const scrollContainer = editor.getScrollerElement();
                    // scrollContainer.scrollTop -= fixedBarHeight;
                }
                newMarkers.push(marker);
                count++;
            }
            setMarkers(newMarkers);
            setMatchCount(count);
        }
        getData();
    }, [searchQuery, matchCase, matchWholeWold, searchIndex]);
    return (
        <Modal
            closeIt = {closeIt}
            title = {fileName}
            actionKey = {content !== initialContent ? "Update" : ""}
            handleFormSubmit = {handleSubmit}
        >
            {showSearchBar?
            <div
                id = "fixed-bar"
                style = {{
                    position: "sticky", 
                    overflowX: "auto",
                    top: 0, 
                    left: 0, 
                    width: "100%", 
                    backgroundColor: "#f0f0f0", 
                    padding: "3px", 
                    zIndex: 1, 
                    display: "flex",
                    alignItems: "center"
                }}
            >
                <div>
                    <span
                        className = "h4 py-0 px-2 font-weight-bold searchBarExpendStyle"
                        onClick = {e => {
                            e.currentTarget.classList.toggle("searchBarExpendRotate");
                            if(showReplaceBar){
                                params.delete("replaceQuery");
                                params.delete("showReplaceBar");
                            }else{
                                params.set("showReplaceBar", true);
                            }
                            history.replace({search: decodeURIComponent(params.toString())});
                        }}
                    >{">"}</span>
                </div>
                <div style = {{
                    display: "flex", 
                    flexDirection: "column", 
                    alignItems: "flex-start",
                }}>
                    <div style = {{
                        display: "flex", 
                        alignItems: "center"
                    }}>
                        <input
                            ref = {searchBarInputRef}
                            type = "text"
                            className = "form-control flex-grow-1"
                            value = {searchText}
                            onChange = {(e) => {
                                const value = e.currentTarget.value;
                                setSearchText(value);
                                params.set("searchQuery", value);
                                if(typingTimeOut){
                                    clearTimeout(typingTimeOut);
                                };
                                if(value === ""){
                                    params.delete("searchQuery");
                                    history.replace({search: decodeURIComponent(params.toString())});
                                }else{
                                    setTypingTimeOut(setTimeout(async () => {
                                        params.set("searchQuery", value);
                                        history.replace({search: decodeURIComponent(params.toString())});
                                    }, 500));
                                }
                                
                                setSearchIndex(0);
                            }}
                            autoComplete = "off"
                            placeholder = "Find"
                            onBlur = {e => {e.currentTarget.placeholder = "Find"}}
                            onFocus = {e => {e.currentTarget.placeholder = "Find (↑↓ for history)"}}
                            onKeyDown = {(event) => {
                                if(event.key === "Enter"){
                                    event.preventDefault();
                                    if(searchIndex < matchCount - 1) setSearchIndex(searchIndex + 1);
                                    else setSearchIndex(0);
                                }
                            }}
                        /> 
                        {matchCount !== 0?<span className = "ml-3">{searchIndex + 1} of {matchCount}</span>:<span className = {"ml-3" + (searchQuery === ""?"":" text-danger")}>No results</span>}
                        <MatchCase
                            className = {`pointer inline ml-3 mb-0 ${matchCase?"bg-primary":""}`}
                            style = {{fill: matchCase?"white":""}}
                            onClick = {() => {
                                if(matchCase){
                                    params.delete("matchCase");
                                }else{
                                    params.set("matchCase", true);
                                }
                                history.replace({search: decodeURIComponent(params.toString())});
                                setSearchIndex(0);
                            }}
                        />
                        <MatchWholeWord
                            className = {`pointer inline ml-3 mb-0 ${matchWholeWold?"bg-primary":""}`}
                            style = {{fill: matchWholeWold?"white":""}}
                            onClick = {() => {
                                if(matchWholeWold){
                                    params.delete("matchWholeWold");
                                }else{
                                    params.set("matchWholeWold", true);
                                }
                                history.replace({search: decodeURIComponent(params.toString())});
                                setSearchIndex(0);
                            }}
                        />
                        <span className = "pointer h4 px-3 py-1 ml-4 handleHover" onClick = {() => {
                            if(searchIndex > 0) setSearchIndex(searchIndex - 1);
                        }}>↑</span>
                        <span className = "pointer h4 px-3 py-1 ml-4 handleHover" onClick = {() => {
                            if(searchIndex < matchCount - 1) setSearchIndex(searchIndex + 1);
                        }}>↓</span>
                        <span className = "pointer h4 px-3 py-1 ml-4 mr-5 handleHover" onClick = {() => {
                            setSearchText("");
                            params.delete("searchBar");
                            params.delete("matchCase");
                            params.delete("matchWholeWold");
                            params.delete("showReplaceBar");
                            params.delete("searchQuery");
                            params.delete("replaceQuery");
                            history.replace({search: decodeURIComponent(params.toString())});
                            setSearchIndex(0);
                        }}>❌</span>
                    </div>
                    {showReplaceBar?
                    <div style = {{
                        display: "flex", 
                        alignItems: "center"
                    }}>
                        <input
                            type = "text"
                            className = "form-control"
                            value = {replaceQuery}
                            onChange = {(e) => {
                                const value = e.currentTarget.value;
                                if(value === "") params.delete("replaceQuery")
                                else params.set("replaceQuery", value);
                                history.replace({search: decodeURIComponent(params.toString())});
                            }}
                            autoComplete = "off"
                            placeholder = "Replace"
                            onBlur = {e => {e.currentTarget.placeholder = "Replace"}}
                            onFocus = {e => {e.currentTarget.placeholder = "Replace (↑↓ for history)"}}
                        /> 
                        <span className = "pointer h4 px-3 py-1 ml-4 handleHover" onClick = {() => {
                            const editor = editorRef.current.editor; // Access the CodeMirror instance
                            if(!editor || !searchQuery) return;
                            // --------------------------------------------------------------------- 
                            const regex = !matchWholeWold && !matchCase ? new RegExp(searchQuery.replace(/[-/\\^$*+?.()|[\]{}]/g, "\\$&"), "gi") : 
                                matchWholeWold && !matchCase ? new RegExp(`\\b${searchQuery.replace(/[-/\\^$*+?.()|[\]{}]/g, "\\$&")}\\b`, "gi") : 
                                !matchWholeWold && matchCase ? new RegExp(searchQuery.replace(/[-/\\^$*+?.()|[\]{}]/g, "\\$&"), "g") : 
                                new RegExp(`\\b${searchQuery.replace(/[-/\\^$*+?.()|[\]{}]/g, "\\$&")}\\b`, "g");
                            // --------------------------------------------------------------------- 
                            const cursor = editor.getSearchCursor(regex, null);
                            let count = 0;
                            while(cursor.findNext()){
                                if(searchIndex === count){
                                    cursor.replace(replaceQuery);
                                    setMatchCount(matchCount - 1);
                                    markers[searchIndex].clear(); // clear current highlight
                                    markers.splice(searchIndex, 1); // remove marker element from array
                                    setMarkers(markers);
                                }else if(searchIndex + 1 === count){
                                    // the index is already updated from splice above
                                    markers[searchIndex] && markers[searchIndex].clear(); // clear impending mark
                                    if(replaceQuery !== ""){
                                        markers[searchIndex] = editor.markText(cursor.from(), cursor.to(), {className: "curHighlight"}); // apply new highlight
                                        editor.scrollIntoView(cursor.from()); // Scroll to the result
                                    }

                                }else if(searchIndex + 2 === count){
                                    if(replaceQuery === ""){
                                        markers[searchIndex] = editor.markText(cursor.from(), cursor.to(), {className: "curHighlight"}); // apply new highlight
                                        editor.scrollIntoView(cursor.from()); // Scroll to the result
                                    }
                                }
                                count++;
                            }
                            if(searchIndex >= matchCount - 1) setSearchIndex(0);
                        }}><Replace/></span>
                        <span className = "pointer h4 px-3 py-1 ml-4 handleHover" onClick = {async() => {
                            await markers.forEach((marker) => marker.clear()); // Clear each marker
                            const editor = editorRef.current.editor; // Access the CodeMirror instance
                            if(!editor || !searchQuery) return;
                            // --------------------------------------------------------------------- 
                            const regex = !matchWholeWold && !matchCase ? new RegExp(searchQuery.replace(/[-/\\^$*+?.()|[\]{}]/g, "\\$&"), "gi") : 
                                matchWholeWold && !matchCase ? new RegExp(`\\b${searchQuery.replace(/[-/\\^$*+?.()|[\]{}]/g, "\\$&")}\\b`, "gi") : 
                                !matchWholeWold && matchCase ? new RegExp(searchQuery.replace(/[-/\\^$*+?.()|[\]{}]/g, "\\$&"), "g") : 
                                new RegExp(`\\b${searchQuery.replace(/[-/\\^$*+?.()|[\]{}]/g, "\\$&")}\\b`, "g");
                            // --------------------------------------------------------------------- 
                            const cursor = editor.getSearchCursor(regex, null);
                            while(cursor.findNext()){
                                cursor.replace(replaceQuery);
                                if (replaceQuery === ""){
                                    cursor.findNext();
                                }
                              }
                            setMatchCount(0);
                            setSearchIndex(0);
                            setMarkers([]);
                        }}><ReplaceAll/></span>
                    </div>
                    :<></>}
                </div>
            </div>
            :<></>}
            <CodeMirror
                // editorDidMount = {(editor) => {
                    // editorInstanceRef.current = editor; // Save the actual CodeMirror instance
                    // console.log("Editor instance options:", editor.options); // Log options to debug
                // }}
                ref = {editorRef}
                options = {{
                    mode: "php",
                    theme: "material",
                    lineNumbers: true, 
                    indentWithTabs: false, // Use spaces instead of actual tab characters
                    smartIndent: false, 
                    indentUnit: 4, 
                    matchBrackets: true, 
                    extraKeys: {
                        "Tab": (cm) => {
                            if(cm.somethingSelected()){
                            cm.indentSelection("add"); // Indent the selected lines
                            }else{
                            cm.replaceSelection("    ", "end"); // Insert 4 spaces
                            }
                        },
                        "Shift-Tab": (cm) => cm.indentSelection("subtract"), // Unindent selected lines
                        "Enter": (cm) => {
                            const cursor = cm.getCursor(); // Get the current cursor position
                            const line = cm.getLine(cursor.line) ; // Get the content of the current line
                            const leadingWhitespace = line.match(/^\s*/)[0]; // Extract leading spaces/tabs
                            cm.replaceSelection("\n" + leadingWhitespace, "end"); // Insert a newline and preserve the indentation
                        },
                        "Ctrl-K": (cm) => {
                            const selections = cm.listSelections();
                            if(selections.length > 0){
                                cm.toggleComment({ indent: true }); // Comment the selection with indented comment mark
                            }
                        },
                        "Esc": () => {
                            closeIt();
                        }, 
                        "Ctrl-S": () => {
                            handleSubmit();
                        },
                        "Ctrl-F": () => {
                            params.set("searchBar", true);
                            history.replace({search: decodeURIComponent(params.toString())})
                            setTimeout(() => {
                                searchBarInputRef.current?.focus();
                                searchBarInputRef.current?.select();
                            }, 0)
                        },
                        "Alt-C": () => {
                            if(showSearchBar){
                                if(matchCase){
                                    params.delete("matchCase");
                                }else{
                                    params.set("matchCase", true);
                                }
                                history.replace({search: decodeURIComponent(params.toString())});
                                setSearchIndex(0);
                            }
                        },
                        "Alt-W": () => {
                            if(showSearchBar){
                                if(matchWholeWold){
                                    params.delete("matchWholeWold");
                                }else{
                                    params.set("matchWholeWold", true);
                                }
                                history.replace({search: decodeURIComponent(params.toString())});
                                setSearchIndex(0);
                            }
                        },
                        fallthrough: ["default"], // Let other shortcuts work
                    }
                }}
                style = {{height: "800px"}}
                value = {content}
                onBeforeChange = {(editor, data, value) => {
                    setContent(value);
                }}
            />
        </Modal>
    );
}